import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Grid, Typography } from '@mui/material';
import { AdapterUploadAvatar } from 'components/form/AdapterUploadAvatar';
import { fData } from 'utils/formatNumber';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { useSnackbar } from 'notistack';

import { useEditMyProfile } from 'queries/Users';
import AdapterField from 'components/form/AdapterFiled';
import { handleDropImageAdapter } from 'utils/media';
import { schema } from './validate';

export default function Form({ onClose, user }) {
  const { enqueueSnackbar } = useSnackbar();

  const [isEdit, setIsEdit] = useState(false);
  const { editMyProfileMutate } = useEditMyProfile();

  const {
    setValue,
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm({
    defaultValues: user,
    resolver: yupResolver(schema),
  });

  const handleClose = () => {
    setIsEdit(false);
    reset();
    onClose();
  };

  Form.propTypes = {
    onClose: PropTypes.func.isRequired,
    user: PropTypes.object.isRequired,
  };

  const onSubmit = async (dataForm) => {
    try {
      await editMyProfileMutate({ dataForm, profileId: user.id });

      handleClose();
      enqueueSnackbar('Profile Edited!');
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || error.message, { variant: 'error' });
    }
  };

  const handleDropAvatar = handleDropImageAdapter({ id: 'iconId', setValue });

  return (
    <form onSubmit={handleSubmit((val) => onSubmit(val))}>
      <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 12 }}>
        <Grid item xs={12}>
          <AdapterUploadAvatar
            required
            control={control}
            name="iconId"
            accept="image/*"
            maxSize={3145728}
            onDrop={handleDropAvatar}
            disabled={!isEdit}
            helperText={
              <Typography
                variant="caption"
                sx={{
                  mt: 2,
                  mx: 'auto',
                  display: 'block',
                  textAlign: 'center',
                  color: 'text.secondary',
                }}
              >
                Allowed *.jpeg, *.jpg, *.png, *.gif
                <br /> max size of {fData(3145728)}
              </Typography>
            }
          />
          <AdapterField
            disabled={!isEdit}
            name="name"
            control={control}
            label="Name"
            margin="normal"
            autoFocus
            fullWidth
            required
          />
          <AdapterField
            disabled={!isEdit}
            name="surname"
            control={control}
            label="Surname"
            margin="normal"
            fullWidth
            required
          />
          <AdapterField
            disabled
            name="email"
            required
            type="email"
            control={control}
            label="Email"
            margin="normal"
            fullWidth
          />
          <AdapterField
            disabled={!isEdit}
            name="username"
            control={control}
            label="User name"
            margin="normal"
            fullWidth
            required
          />
        </Grid>
      </Grid>

      <Grid spacing={5} sx={{ mt: 2 }} container item xs={12}>
        <Grid item>
          <Button
            type="submit"
            style={{ display: 'block', textTransform: 'uppercase', padding: '10px 30px' }}
            variant="contained"
            disabled={!isEdit || isSubmitting}
          >
            Submit
          </Button>
        </Grid>
        <Grid item>
          <Button
            type="button"
            variant="contained"
            onClick={handleClose}
            color="error"
            sx={{ display: 'block', textTransform: 'uppercase', padding: '10px 30px' }}
            disabled={!isEdit}
          >
            Cancel
          </Button>
        </Grid>
      </Grid>
      <Button
        type="button"
        variant="contained"
        onClick={() => {
          setIsEdit(true);
        }}
        color="info"
        sx={{ display: 'block', textTransform: 'uppercase', padding: '10px 30px', mt: 4 }}
        disabled={isEdit}
      >
        Edit
      </Button>
    </form>
  );
}
